/**
 * JourneyCallbackContext.tsx — lets another control on a question screen open
 * the journey's ONE call-back dialog (CTO, 2026-09-24: "give the callback option
 * … inside this modal pop-up", looking at the "Why we ask" dialog).
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY A CONTEXT AND NOT A SECOND FORM. The call-back lane already exists on this
 * screen: `JourneyCallbackOffer`, the footer QuestionFlow renders, owns a
 * `DialogShell` holding the shared `CallbackForm` (the same POST, the same lead
 * and attribution, the same confirmation email). A second form inside "Why we
 * ask" would be a second lane to keep in step, and a link to `/:slug/callback`
 * would unmount the in-memory journey. So the offer PUBLISHES its own `open()`
 * here and "Why we ask" calls it: one dialog, one form, one request.
 *
 * WHO IS WHO.
 *  - `JourneyCallbackProvider` — mounted by QuestionFlow around a question
 *    screen, and only when it has an offer to render. The staff fill-on-behalf
 *    flow passes none, so it gets no provider. Since 2026-09-25 JourneyPage
 *    also mounts one around the whole journey stage, for Step 0: its offer sits
 *    under the stage, and the "Leave this step?" retention dialog there hands
 *    off to it. QuestionFlow's provider is nearer, so a question screen still
 *    reads its own.
 *  - `usePublishJourneyCallback` — called by `JourneyCallbackOffer`. Outside a
 *    provider it does nothing; under JourneyPage's (the projection, the result,
 *    checkout) it publishes to a provider nothing on those screens reads.
 *  - `useJourneyCallback` — read by `WhyWeAsk` and by the journey variant of
 *    `navigation/LeaveConfirmDialog` (2026-09-25). `null` means "no call-back on
 *    this screen" (no provider, or no offer mounted under it — the capture
 *    loader withdraws Step 0's), and the caller must then offer nothing: a
 *    button with no dialog behind it is worse than no button.
 *
 * Two contexts, so the offer (which only publishes) does not re-render every
 * time what it published changes.
 *
 * WHETHER IT IS OPEN, AND HOW TO CLOSE IT (review, 2026-09-25). The offer also
 * publishes `isOpen` and `close()`, for one reader: the journey leave dialog.
 * A patient who reached the call-back dialog by pressing Back (the hand-off)
 * will often press Back again to get out of it — on Android that is the usual
 * way to dismiss a dialog. The blocked navigation then mounted a SECOND leave
 * dialog, and it sat UNDER the call-back dialog (both are DialogShell's
 * z-index 1000 layer, and the offer comes later in the DOM) while its mount
 * effect took focus behind the scrim. With `isOpen` the leave dialog sees the
 * call-back dialog is up and does what Back means on a dialog: it closes that
 * one and stays. One dialog at a time, however many times Back is pressed.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

export interface JourneyCallback {
  /** True once this journey has sent a request — JourneyPage owns the flag. */
  requested: boolean;
  /**
   * Open the call-back dialog in place. `returnFocusTo` is where focus goes when
   * the patient closes it WITHOUT sending: the control that led them there has
   * usually gone (it lived in a dialog that closed to make way). After a send,
   * focus goes to the offer's thank-you line, as it always has.
   */
  open: (returnFocusTo?: HTMLElement | null) => void;
  /** True while the offer's call-back dialog is on screen. */
  isOpen: boolean;
  /**
   * Close the call-back dialog as its own "Not now" (or, after a send, "Back to
   * where I was") would: nothing is sent, and focus goes where it would have gone.
   */
  close: () => void;
}

type Publish = Dispatch<SetStateAction<JourneyCallback | null>>;

const PublishContext = createContext<Publish | null>(null);
const CallbackContext = createContext<JourneyCallback | null>(null);

export function JourneyCallbackProvider({ children }: { children: ReactNode }) {
  const [callback, setCallback] = useState<JourneyCallback | null>(null);
  return (
    <PublishContext.Provider value={setCallback}>
      <CallbackContext.Provider value={callback}>{children}</CallbackContext.Provider>
    </PublishContext.Provider>
  );
}

/** The screen's call-back, or `null` when there is none to offer. */
export function useJourneyCallback(): JourneyCallback | null {
  return useContext(CallbackContext);
}

/**
 * Publish the offer's call-back to the screen. Withdrawn on unmount — but only
 * if it is still the one published, so a replacement offer mounting in the same
 * commit is never wiped by its predecessor's cleanup. `open` and `close` must be
 * referentially stable (`useCallback`), or every render would republish.
 */
export function usePublishJourneyCallback({ requested, open, isOpen, close }: JourneyCallback): void {
  const publish = useContext(PublishContext);
  useEffect(() => {
    if (!publish) return undefined;
    const mine: JourneyCallback = { requested, open, isOpen, close };
    publish(mine);
    return () => publish((current) => (current === mine ? null : current));
  }, [publish, requested, open, isOpen, close]);
}
